/**
 * IndustryArchitectTip — collapsible inline tip on the Industry Architect
 * page. Auto-dismisses once the user saves a non-empty profile (the
 * backend's checklist computation flips `industry_architect.done` to true
 * the moment any meaningful field is saved; this component listens to that).
 *
 * Existing users never see it — `architect_tip_dismissed` stays false but
 * they don't pass the `should_show_signature_nudge`-style gate because
 * their checklist's industry_architect entry is already done. We render
 * only when the profile is empty AND the user hasn't dismissed it AND
 * onboarding_complete is false.
 */
import { useOnboarding } from '../../context/OnboardingContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Lightbulb, X } from 'lucide-react';

export default function IndustryArchitectTip() {
  const { state, dismissArchitectTip } = useOnboarding();

  if (!state) return null;
  if (state.onboarding_complete) return null;
  if (state.architect_tip_dismissed) return null;
  // Hide once the profile is saved (i.e. checklist item flips done).
  const item = state.checklist?.find((c) => c.key === 'industry_architect');
  if (item?.done) return null;

  return (
    <Card
      className="rounded-sm border-l-4 border-l-[#B89B5E] border-y border-r bg-[#FAF5E8]/30"
      data-testid="industry-architect-tip"
    >
      <CardContent className="py-3 px-4">
        <div className="flex items-start gap-3">
          <Lightbulb className="h-5 w-5 text-[#B89B5E] shrink-0 mt-0.5" />
          <div className="flex-1 text-sm text-stone-700">
            <p className="font-medium text-stone-900">
              Fill this out once — it powers everything.
            </p>
            <ul className="mt-2 space-y-1 text-stone-600 leading-relaxed">
              <li>
                <strong>Industry & ICP</strong> — sweeps filter for signals
                from companies that match your buyer profile.
              </li>
              <li>
                <strong>Value drivers & differentiators</strong> — used by
                the AI proposal generator and the executive brief on every
                opportunity.
              </li>
              <li>
                <strong>Service area</strong> — keeps sweeps geographically
                relevant so you don&apos;t chase signals you can&apos;t serve.
              </li>
            </ul>
            <p className="mt-2 text-xs text-stone-500">
              You can come back and refine this anytime. This tip disappears
              once you save.
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={dismissArchitectTip}
            className="shrink-0 -mt-1 text-stone-400 hover:text-stone-700"
            data-testid="industry-architect-tip-dismiss"
            aria-label="Dismiss tip"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
